import {
  Bell,
  Building2,
  CheckCircle,
  ChevronDown,
  Clock,
  FileText,
  KeyRound,
  Lightbulb,
  Mail,
  MessageSquare,
  Phone,
  Router,
  StickyNote,
  User,
} from "@tamagui/lucide-icons";
import {
  API,
  ServiceRequestStatus,
  useHubs,
  useSensors,
  useServiceRequest,
  useUpdateServiceRequest,
} from "common";
import { updateSensorAlertAcknowledgedState } from "common/src/graphql/mutations";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import {
  Anchor,
  Avatar,
  Button,
  Card,
  ListItem,
  Popover,
  ScrollView,
  Separator,
  Spinner,
  Text,
  XStack,
  YGroup,
  YStack,
} from "tamagui";
import { GradientButton } from "tamagui-config";

import ServiceRequestChangeStatusModal from "./ServiceRequestChangeStatusModal";

enum ServiceRequestAction {
  ACCEPT = "accept",
  EDIT = "edit",
  SENSOR_DETAILS = "sensorDetails",
  SERVICE_HISTORY = "serviceHistory",
  UPLOAD_IMAGE = "uploadImage",
  SEND_PUSH = "sendPush",
  NOTIFY_CUSTOMER = "notifyCustomer",
}

const ServiceRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [acceptingJob, setAcceptingJob] = useState(false);
  const [startingJob, setStartingJob] = useState(false);
  const [closingRequest, setClosingRequest] = useState(false);
  const { t } = useTranslation();

  const { serviceRequest, loading, error, refetch } = useServiceRequest(
    id || "",
  );
  const { update: updateServiceRequest } = useUpdateServiceRequest();

  const { hubs, fetchHubs } = useHubs({ fetchAndSubscribeOnLoad: false });
  const { sensors, fetchSensorsByHub } = useSensors();

  const hubDetails = useMemo(() => {
    if (!serviceRequest?.hubId || !hubs.length) return null;
    return hubs.find((hub) => hub.id === serviceRequest.hubId);
  }, [serviceRequest?.hubId, hubs]);

  const sensorDetails = useMemo(() => {
    if (!serviceRequest?.sensorId || !sensors.length) return null;
    return sensors.find((sensor) => sensor.id === serviceRequest.sensorId);
  }, [serviceRequest?.sensorId, sensors]);

  useEffect(() => {
    if (serviceRequest?.hubId) {
      fetchHubs();
      fetchSensorsByHub(serviceRequest.hubId);
    }
  }, [serviceRequest?.hubId]);

  const handleAction = async (action: ServiceRequestAction) => {
    switch (action) {
      case ServiceRequestAction.ACCEPT:
        await handleAcceptServiceRequest();
        break;

      case ServiceRequestAction.EDIT:
        setIsModalOpen(true);
        break;

      case ServiceRequestAction.SENSOR_DETAILS:
        handleSeeSensorAndAlertDetails();
        break;

      case ServiceRequestAction.SERVICE_HISTORY:
        handleSeeServiceHistory();
        break;

      case ServiceRequestAction.UPLOAD_IMAGE:
        // TODO: Open image upload dialog
        console.log(
          "Opening image upload for service request:",
          serviceRequest?.id,
        );
        break;

      case ServiceRequestAction.SEND_PUSH:
        // TODO: Send push notification on schedule
        console.log(
          "Sending push notification for service request:",
          serviceRequest?.id,
        );
        break;

      case ServiceRequestAction.NOTIFY_CUSTOMER:
        // TODO: Send notification to customer
        console.log(
          "Notifying customer for service request:",
          serviceRequest?.id,
        );
        break;

      default:
        console.warn("Unknown action:", action);
        break;
    }

    setActionMenuOpen(false);
  };

  const handleUpdateServiceRequestStatus = useCallback(
    async ({ status }: { status: string }) => {
      if (!serviceRequest) return;

      try {
        await updateServiceRequest({
          id: serviceRequest.id,
          status,
        });
        console.log("Service request updated successfully");
        await refetch();
      } catch (err) {
        console.error("Error updating service request:", err);
      }
    },
    [serviceRequest, updateServiceRequest, refetch],
  );

  const handleAcceptServiceRequest = async () => {
    if (!serviceRequest) return;
    
    setAcceptingJob(true);
    try {
      await updateServiceRequest({
        id: serviceRequest.id,
        status: ServiceRequestStatus.OPEN,
      });
      await refetch();
      console.log("Service request accepted successfully");
    } catch (err) {
      console.error("Error accepting service request:", err);
    } finally {
      setAcceptingJob(false);
    }
  };

  const handleStartJob = async () => {
    if (!serviceRequest) return;

    setStartingJob(true);
    try {
      await updateServiceRequest({
        id: serviceRequest.id,
        status: ServiceRequestStatus.IN_PROGRESS,
      });
      await refetch();
      console.log("Service request started successfully");
    } catch (err) {
      console.error("Error starting service request:", err);
    } finally {
      setStartingJob(false);
    }
  };

  const handleCloseServiceRequest = async () => {
    if (!serviceRequest) return;

    if (serviceRequest.status === ServiceRequestStatus.CLOSED) return;

    setClosingRequest(true);
    try {
      await updateServiceRequest({
        id: serviceRequest.id,
        status: ServiceRequestStatus.CLOSED,
      });

      if (serviceRequest.alertId) {
        try {
          await API.graphql({
            query: updateSensorAlertAcknowledgedState,
            variables: {
              input: {
                id: serviceRequest.alertId,
                isAcknowledged: true,
              },
            },
          });
        } catch (alertError) {
          console.error("Error auto-acknowledging alert:", alertError);
        }
      }

      navigate("/service-requests");
    } catch (err) {
      console.error("Error closing service request:", err);
    } finally {
      setClosingRequest(false);
    }
  };

  const handleSeeSensorAndAlertDetails = () => {
    if (serviceRequest?.alertId)
      return navigate(`/alerts/${serviceRequest?.alertId}`);
    navigate(
      `base-stations/${serviceRequest?.hubId}/sensors/${serviceRequest?.sensorId}/events`,
    );
  };

  const handleSeeServiceHistory = () => {
    navigate(`/service-requests?customer=${serviceRequest?.customerId}`);
  };

  if (loading && !serviceRequest) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center">
        <Text fontSize="$6">Loading...</Text>
      </YStack>
    );
  }

  if (error) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center">
        <Text fontSize="$6" color="$red10">
          Error: {error.message}
        </Text>
      </YStack>
    );
  }

  // Format date and time from timestamp
  const formatDateTime = (timestamp?: number): string => {
    if (!timestamp) return "Not specified";
    return new Date(timestamp).toLocaleString();
  };

  // Get service request title based on available data
  const getServiceRequestTitle = (): string => {
    if (!serviceRequest) return "Service Request Details";

    if (serviceRequest.serviceType) {
      return t(`serviceRequests.serviceTypes.${serviceRequest.serviceType}`);
    }

    const jobNumber = (serviceRequest as any).jobNumber;
    const id = serviceRequest.id;

    if (jobNumber) {
      return `Service Request #${jobNumber}`;
    }

    if (id) {
      return `Service Request #${id.substring(0, 8)}`;
    }

    return "Service Request Details";
  };

  const InfoCard: React.FC<{
    icon: React.ElementType;
    title: string;
    children: React.ReactNode;
    width?: string | number;
    $mdWidth?: string | number;
  }> = ({ icon: Icon, title, children, width = "100%", $mdWidth }) => (
    <Card
      elevate
      bordered
      padding="$3"
      marginBottom="$3"
      width={width}
      $md={{ width: $mdWidth || "23%" }}
    >
      <XStack alignItems="center" marginBottom="$2">
        <Icon size="$1" color="$gray10" marginRight="$2" />
        <Text fontWeight="bold" fontSize="$5">
          {title}
        </Text>
      </XStack>
      {children}
    </Card>
  );

  const DetailSectionCard: React.FC<{
    icon: React.ElementType;
    title: string;
    children: React.ReactNode;
  }> = ({ icon: Icon, title, children }) => (
    <Card elevate bordered padding="$3" marginBottom="$3">
      <XStack alignItems="center" marginBottom="$2">
        <Icon size="$1.5" color="$gray11" marginRight="$2" />
        <Text fontSize="$6" fontWeight="bold">
          {title}
        </Text>
      </XStack>
      {children}
    </Card>
  );

  return (
    <ScrollView>
      <YStack padding="$4" space="$4" maxWidth={1200} marginHorizontal="auto">
        <XStack justifyContent="space-between" alignItems="center">
          <YStack>
            <Text fontSize="$5" color="$gray10">
              Service Requests
            </Text>
            <Text fontSize="$8" fontWeight="bold">
              {getServiceRequestTitle()}
            </Text>
          </YStack>
          <XStack space="$3">
            <Popover
              open={actionMenuOpen}
              onOpenChange={setActionMenuOpen}
              placement="bottom-end"
            >
              <Popover.Trigger asChild>
                <GradientButton
                  theme="active"
                  iconAfter={ChevronDown}
                  onPress={() => setActionMenuOpen(true)}
                >
                  {t("serviceRequests.actions.action")}
                </GradientButton>
              </Popover.Trigger>
              <Popover.Content elevate bordered padding="$0" borderRadius="$4">
                <Popover.Arrow />
                <YGroup>
                  {serviceRequest?.status === ServiceRequestStatus.PENDING && (
                    <YGroup.Item>
                      <ListItem
                        title={acceptingJob ? t("serviceRequests.actions.accepting") : t("serviceRequests.actions.acceptJob")}
                        disabled={acceptingJob}
                        onPress={() =>
                          !acceptingJob && handleAction(ServiceRequestAction.ACCEPT)
                        }
                        iconAfter={acceptingJob ? <Spinner size="small" /> : undefined}
                        hoverStyle={{
                          backgroundColor: "$white7",
                        }}
                        pressStyle={{
                          backgroundColor: "$white7",
                          scale: 0.97,
                        }}
                      />
                    </YGroup.Item>
                  )}
                  <YGroup.Item>
                    <ListItem
                      title={t("serviceRequests.actions.editServiceRequest")}
                      onPress={() => handleAction(ServiceRequestAction.EDIT)}
                      hoverStyle={{
                        backgroundColor: "$white7",
                      }}
                      pressStyle={{
                        backgroundColor: "$white7",
                        scale: 0.97,
                      }}
                    />
                  </YGroup.Item>
                  <YGroup.Item>
                    <ListItem
                      title={t("serviceRequests.actions.seeSensorAlertDetails")}
                      onPress={() =>
                        handleAction(ServiceRequestAction.SENSOR_DETAILS)
                      }
                      hoverStyle={{
                        backgroundColor: "$white7",
                      }}
                      pressStyle={{
                        backgroundColor: "$white7",
                        scale: 0.97,
                      }}
                    />
                  </YGroup.Item>
                  <YGroup.Item>
                    <ListItem
                      title={t("serviceRequests.actions.seeServiceHistory")}
                      onPress={() =>
                        handleAction(ServiceRequestAction.SERVICE_HISTORY)
                      }
                      hoverStyle={{
                        backgroundColor: "$white7",
                      }}
                      pressStyle={{
                        backgroundColor: "$white7",
                        scale: 0.97,
                      }}
                    />
                  </YGroup.Item>
                  {/* <YGroup.Item>
                    <ListItem
                      hoverTheme
                      pressTheme
                      title="Follow to Upload image"
                      onPress={() =>
                        handleAction(ServiceRequestAction.UPLOAD_IMAGE)
                      }
                    />
                  </YGroup.Item> */}
                  {/* <YGroup.Item>
                    <ListItem
                      hoverTheme
                      pressTheme
                      title="Send Fast Push on Schedule"
                      onPress={() =>
                        handleAction(ServiceRequestAction.SEND_PUSH)
                      }
                    />
                  </YGroup.Item> */}
                  {/* <YGroup.Item>
                    <ListItem
                      hoverTheme
                      pressTheme
                      title="Notify Customer"
                      onPress={() =>
                        handleAction(ServiceRequestAction.NOTIFY_CUSTOMER)
                      }
                    />
                  </YGroup.Item> */}
                </YGroup>
              </Popover.Content>
            </Popover>
            {serviceRequest?.status === ServiceRequestStatus.OPEN && (
              <Button
                onPress={handleStartJob}
                chromeless
                borderColor="$green10"
                borderWidth={1}
                color="$green10"
                disabled={startingJob}
                iconAfter={startingJob ? <Spinner size="small" color="$green10" /> : undefined}
              >
                {startingJob ? t("serviceRequests.actions.starting") : t("serviceRequests.actions.startJob")}
              </Button>
            )}
            {serviceRequest?.status !== ServiceRequestStatus.CLOSED && (
              <Button
                onPress={handleCloseServiceRequest}
                chromeless
                borderColor="$blue10"
                borderWidth={1}
                color="$blue10"
                disabled={closingRequest}
                iconAfter={closingRequest ? <Spinner size="small" color="$blue10" /> : undefined}
              >
                {closingRequest ? t("serviceRequests.actions.closing") : t("serviceRequests.actions.closeServiceRequest")}
              </Button>
            )}
          </XStack>
        </XStack>

        <XStack
          flexWrap="wrap"
          justifyContent="space-between"
          marginHorizontal="$-1.5"
        >
          <InfoCard
            icon={CheckCircle}
            title="Status"
            $mdWidth="24%"
            width="100%"
          >
            <XStack alignItems="center" space="$2">
              <XStack
                alignItems="center"
                justifyContent="center"
                backgroundColor={
                  serviceRequest?.status === ServiceRequestStatus.OPEN
                    ? "$green7"
                    : serviceRequest?.status === ServiceRequestStatus.PENDING
                      ? "$orange7"
                      : serviceRequest?.status ===
                          ServiceRequestStatus.IN_PROGRESS
                        ? "$hgPrimary"
                        : serviceRequest?.status === ServiceRequestStatus.CLOSED
                          ? "$gray7"
                          : "$purple7"
                }
                paddingHorizontal="$2"
                paddingVertical="$1"
                borderRadius="$10"
              >
                <Text fontSize="$2" color={"$color1"}>
                  {serviceRequest?.status === ServiceRequestStatus.IN_PROGRESS
                    ? t("serviceRequests.statuses.inProgress")
                    : serviceRequest?.status === ServiceRequestStatus.OPEN
                      ? t("serviceRequests.statuses.open")
                      : serviceRequest?.status === ServiceRequestStatus.PENDING
                        ? t("serviceRequests.statuses.pending")
                        : serviceRequest?.status === ServiceRequestStatus.CLOSED
                          ? t("serviceRequests.statuses.closed")
                          : serviceRequest?.status ===
                              ServiceRequestStatus.CANCELED
                            ? t("serviceRequests.statuses.canceled")
                            : serviceRequest?.status ===
                                ServiceRequestStatus.RESCHEDULE
                              ? t("serviceRequests.statuses.reschedule")
                              : serviceRequest?.status || "Unknown"}
                </Text>
              </XStack>
              {(serviceRequest as any)?.priority && (
                <Text fontSize="$3" color="$gray10">
                  Priority Level: {(serviceRequest as any).priority}
                </Text>
              )}
            </XStack>
            {serviceRequest?.status === ServiceRequestStatus.CLOSED && (
              <YStack marginTop="$2">
                <Text fontSize="$4">
                  Closed at:{" "}
                  {serviceRequest?.completedAt
                    ? formatDateTime(Number(serviceRequest.completedAt))
                    : "Unknown"}
                </Text>
              </YStack>
            )}
          </InfoCard>
          {/* Negative margin to counteract padding on cards for spacing */}
          <InfoCard
            icon={Router}
            title="Sensor Type"
            $mdWidth="24%"
            width="100%"
          >
            <Text fontSize="$4">
              {serviceRequest?.serviceType
                ? t(
                    `serviceRequests.serviceTypes.${serviceRequest.serviceType}`,
                  )
                : "Not specified"}
            </Text>
            <Text fontSize="$4">
              <Text fontWeight="bold">
                {t("serviceRequests.detail.sensorId")}
              </Text>{" "}
              {serviceRequest?.sensorId || t("common.notSpecified")}
            </Text>
            <Text fontSize="$4">
              <Text fontWeight="bold">
                {t("serviceRequests.detail.sensorName")}
              </Text>{" "}
              {sensorDetails?.name || "Not Provided"}
            </Text>
            <Text fontSize="$4">
              <Text fontWeight="bold">
                {t("serviceRequests.detail.roomLocation")}
              </Text>{" "}
              {sensorDetails?.location || "Not Provided"}
            </Text>
            <Text fontSize="$4">
              <Text fontWeight="bold">
                {t("serviceRequests.detail.sublocation")}
              </Text>{" "}
              {sensorDetails?.sublocation || "Not Provided"}
            </Text>
            <Text fontSize="$4">
              <Text fontWeight="bold">
                {t("serviceRequests.detail.sensorType")}
              </Text>{" "}
              {sensorDetails?.type || "Not Provided"}
            </Text>
          </InfoCard>
          <InfoCard icon={Bell} title="Alert" $mdWidth="24%" width="100%">
            <Text fontSize="$4">
              {(serviceRequest as any)?.reason || "Not specified"}
            </Text>
            {(serviceRequest as any)?.alertId && (
              <YStack gap="$2">
                <Text fontSize="$3" color="$gray10">
                  Alert ID: {(serviceRequest as any).alertId}
                </Text>
                <Anchor
                  fontSize="$3"
                  color="$blue10"
                  textDecorationLine="underline"
                  cursor="pointer"
                  onPress={() =>
                    navigate(`/alerts/${(serviceRequest as any).alertId}`)
                  }
                >
                  View Alert Details
                </Anchor>
              </YStack>
            )}
          </InfoCard>
          <InfoCard
            icon={Router}
            title={t("serviceRequests.detail.hubInformation")}
            $mdWidth="48%"
            width="100%"
          >
            <Text fontSize="$4">
              <Text fontWeight="bold">{t("serviceRequests.detail.hubId")}</Text>{" "}
              {serviceRequest?.hubId || "Not Provided"}
            </Text>
            <Text fontSize="$4">
              <Text fontWeight="bold">
                {t("serviceRequests.detail.hubName")}
              </Text>{" "}
              {hubDetails?.name || "Not Provided"}
            </Text>
            <Text fontSize="$4">
              <Text fontWeight="bold">
                {t("serviceRequests.detail.hubLocation")}
              </Text>{" "}
              {hubDetails && (hubDetails.street || hubDetails.city)
                ? [
                    hubDetails.street,
                    hubDetails.city,
                    hubDetails.state,
                    hubDetails.zip,
                  ]
                    .filter(Boolean)
                    .join(", ")
                : "Not Provided"}
            </Text>
          </InfoCard>
          <InfoCard
            icon={Clock}
            title="Customer Request"
            $mdWidth="24%"
            width="100%"
          >
            <Text fontSize="$4">
              {serviceRequest?.createdAt
                ? formatDateTime(new Date(serviceRequest.createdAt).getTime())
                : "Not available"}
            </Text>
          </InfoCard>
        </XStack>

        <Separator marginVertical="$3" />

        <XStack space="$4" flexWrap="wrap" $sm={{ flexDirection: "column" }}>
          <YStack space="$3" width="100%" $md={{ width: "65%" }}>
            <DetailSectionCard icon={User} title="Customer Information">
              {serviceRequest?.customerId ? (
                <>
                  <Text fontSize="$4">
                    <Text fontWeight="bold">Name:</Text>{" "}
                    {serviceRequest.customerName ||
                      (serviceRequest as any).customer?.name ||
                      (serviceRequest as any).customer?.companyName ||
                      "Not provided"}
                  </Text>
                  <Text fontSize="$4">
                    <Text fontWeight="bold">Email:</Text>{" "}
                    {(serviceRequest as any).customer?.email || "Not provided"}
                  </Text>
                  <Text fontSize="$4">
                    <Text fontWeight="bold">Phone:</Text>{" "}
                    {(serviceRequest as any).customer?.phone || "Not provided"}
                  </Text>
                </>
              ) : (
                <Text fontSize="$4">Customer information not available</Text>
              )}
            </DetailSectionCard>

            {(serviceRequest as any)?.customerComplaint && (
              <DetailSectionCard icon={FileText} title="Complaint">
                <Text fontSize="$4">
                  {(serviceRequest as any).customerComplaint}
                </Text>
              </DetailSectionCard>
            )}

            {(serviceRequest as any)?.customerNotes && (
              <DetailSectionCard icon={StickyNote} title="Customer Notes">
                <Text fontSize="$4">
                  {(serviceRequest as any).customerNotes}
                </Text>
              </DetailSectionCard>
            )}

            {serviceRequest?.notes && (
              <DetailSectionCard icon={StickyNote} title="Notes">
                <Text fontSize="$4">{serviceRequest.notes}</Text>
              </DetailSectionCard>
            )}

            {(serviceRequest as any)?.potentialCause && (
              <DetailSectionCard icon={Lightbulb} title="Potential Cause">
                <Text fontSize="$4">
                  {(serviceRequest as any).potentialCause}
                </Text>
              </DetailSectionCard>
            )}

            {(serviceRequest as any)?.accessInstructions && (
              <DetailSectionCard icon={KeyRound} title="Access Instructions">
                <Text fontSize="$4">
                  {(serviceRequest as any).accessInstructions}
                </Text>
              </DetailSectionCard>
            )}

            {(serviceRequest as any)?.additionalNotes && (
              <DetailSectionCard icon={MessageSquare} title="Additional Notes">
                <Text fontSize="$4">
                  {(serviceRequest as any).additionalNotes}
                </Text>
              </DetailSectionCard>
            )}
          </YStack>

          <YStack width="100%" $md={{ width: "32%" }}>
            {(serviceRequest as any)?.serviceCompany ? (
              <Card elevate bordered padding="$3">
                <XStack alignItems="center" marginBottom="$2">
                  <Building2 size="$1.5" color="$gray11" marginRight="$2" />
                  <Text fontSize="$6" fontWeight="bold">
                    Service Company
                  </Text>
                </XStack>
                <XStack alignItems="center" marginBottom="$3" space="$3">
                  <Avatar circular size="$5">
                    <Avatar.Fallback
                      backgroundColor="$blue5"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {(serviceRequest as any).serviceCompany.name
                        ?.substring(0, 2)
                        .toUpperCase() || "SC"}
                    </Avatar.Fallback>
                  </Avatar>
                  <Text fontSize="$5" fontWeight="500">
                    {(serviceRequest as any).serviceCompany.name ||
                      "Unknown Company"}
                  </Text>
                </XStack>
                <YGroup separator={<Separator />}>
                  {(serviceRequest as any).serviceCompany.phone && (
                    <YGroup.Item>
                      <ListItem
                        hoverTheme
                        icon={<Phone size="$1" color="$gray10" />}
                        title={(serviceRequest as any).serviceCompany.phone}
                        pressTheme={false}
                      />
                    </YGroup.Item>
                  )}
                  {(serviceRequest as any).serviceCompany.email && (
                    <YGroup.Item>
                      <ListItem
                        hoverTheme
                        icon={<Mail size="$1" color="$gray10" />}
                        title={
                          <Anchor
                            href={`mailto:${(serviceRequest as any).serviceCompany.email}`}
                            textDecorationLine="none"
                            color="$blue10"
                            hoverStyle={{ color: "$blue11" }}
                          >
                            {(serviceRequest as any).serviceCompany.email}
                          </Anchor>
                        }
                        pressTheme={false}
                      />
                    </YGroup.Item>
                  )}
                </YGroup>
              </Card>
            ) : (
              <Card elevate bordered padding="$3">
                <XStack alignItems="center" marginBottom="$2">
                  <Building2 size="$1.5" color="$gray11" marginRight="$2" />
                  <Text fontSize="$6" fontWeight="bold">
                    Service Company
                  </Text>
                </XStack>
                <Text fontSize="$4" color="$gray10">
                  No service company assigned
                </Text>
              </Card>
            )}
          </YStack>
        </XStack>
      </YStack>
      <ServiceRequestChangeStatusModal
        open={isModalOpen}
        serviceRequest={serviceRequest}
        onOpenChange={setIsModalOpen}
        onSubmit={handleUpdateServiceRequestStatus}
      />
    </ScrollView>
  );
};

export default ServiceRequestDetail;
